import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import { CompleteActivePlanOfCarePeriod } from "../types";
import { DateTime } from "luxon";

export type PocInfo = {
  gap: number;
  numOfAppointments: number;
  appointments: {
    status: "occurred" | "scheduled" | "unscheduled" | "unauthorized";
    appointmentTypeId: string;
    appointmentDuration: number;
    singleBookAppointment: boolean;
    isEval: boolean;
    start: string | null;
  }[];
  periodStart: string;
  periodEnd: string;
  currentWeek: number;
  totalWeeks: number;
};

export type Provider = {
  id: string;
  penciledId: string;
  name: string;
  appointmentTypesAccepted: string[];
  isSupervisingProvider: boolean;
};

export type BookingState = {
  currentAppointment: number;
  appointmentBooked: {
    start: DateTime;
    end: DateTime;
    appointmentNumber: number;
    providerId: string;
    appointmentTypeId: string;
    error: boolean;
    confirmed: boolean;
    availabilityEventId?: string;
  }[];
  waitlist: {
    start: DateTime;
    end: DateTime;
    appointmentNumber: number;
    appointmentTypeId: string;
    error: boolean;
    confirmed: boolean;
  }[];
};

type PocState = {
  loading: boolean;
  updating: boolean;
  pocInfo: PocInfo;
  providerInfo: Provider[];
  preferredSupervisingProvider: {
    id: string;
    name: string;
  } | null;
  preferredNonSupervisingProvider: {
    id: string;
    name: string;
  } | null;
  error: string | null;
  errorMessage: string | null;
  booking: boolean;
  bookingLoading: boolean;
  confirmed: boolean;
  complete: boolean;
  bookingState: BookingState;
};

export const fetchPeriod = createAsyncThunk<
  {
    pocInfo: PocInfo;
    providerInfo: Provider[];
    preferredSupervisingProvider: { id: string; name: string } | null;
    preferredNonSupervisingProvider: { id: string; name: string } | null;
  },
  { uniqueId: string },
  { rejectValue: { error: string; errorMessage: string } }
>("bookPOC/fetchPeriod", async ({ uniqueId }, { rejectWithValue }) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL}/api/poc-links?unique_id=${uniqueId}`
    );
    return {
      pocInfo: response.data.pocInfo,
      providerInfo: response.data.providerInfo,
      preferredSupervisingProvider: response.data.preferredSupervisingProvider,
      preferredNonSupervisingProvider:
        response.data.preferredNonSupervisingProvider
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      return rejectWithValue({
        error: error.response?.data.error || "An unknown error occurred",
        errorMessage: error.response?.data.message || "Please try again."
      });
    }
    return rejectWithValue({
      error: "An unknown error occurred",
      errorMessage: "Please try again."
    });
  }
});

export const updatePeriod = createAsyncThunk<
  { uniqueId: string },
  { uniqueId: string },
  { rejectValue: { error: string; errorMessage: string } }
>("bookPOC/updatePeriod", async ({ uniqueId }, { rejectWithValue }) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/api/poc-links`,
      { unique_id: uniqueId }
    );
    return { uniqueId: response.data.uniqueId };
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 400) {
      }
      return rejectWithValue({
        error: error.response?.data.error || "An unknown error occurred",
        errorMessage: error.response?.data.message || "Please try again."
      });
    }
    return rejectWithValue({
      error: "An unknown error occurred",
      errorMessage: "Please try again."
    });
  }
});

export const bookAppointments = createAsyncThunk<
  {
    confirmedWaitlist: BookingState["waitlist"];
    confirmedAppointments: BookingState["appointmentBooked"];
  },
  { uniqueId: string; bookingState: BookingState },
  {
    rejectValue: {
      confirmedWaitlist: BookingState["waitlist"];
      confirmedAppointments: BookingState["appointmentBooked"];
    };
  }
>(
  "bookPOC/bookAppointments",
  async ({ uniqueId, bookingState }, { rejectWithValue }) => {
    let confirmedWaitlist: BookingState["waitlist"] = [];
    let confirmedAppointments: BookingState["appointmentBooked"] = [];
    try {
      const pendingWaitlist = bookingState.waitlist.filter(
        (waitlist) => !waitlist.confirmed
      );
      for (const waitlistItem of pendingWaitlist) {
        let start = waitlistItem.start;
        let end = waitlistItem.end;

        let dates = [];

        while (start <= end) {
          dates.push(start.toISO());
          start = start.plus({ days: 1 });
        }

        await axios.patch(
          `${process.env.REACT_APP_BACKEND_URL}/api/poc-links/availability`,
          {
            unique_id: uniqueId,
            dates: dates,
            appointment_type_id: waitlistItem.appointmentTypeId
          }
        );
        confirmedWaitlist.push(waitlistItem);
      }
      const pendingAppointments = bookingState.appointmentBooked.filter(
        (appointment) => !appointment.confirmed
      );
      for (const appointment of pendingAppointments) {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/poc-links/book-appointment`,
          {
            unique_id: uniqueId,
            start_date: appointment.start.toISO(),
            duration: appointment.end.diff(appointment.start).as("minutes"),
            provider_id: appointment.providerId,
            appointment_type_id: appointment.appointmentTypeId,
            availability_block_id: appointment.availabilityEventId
          }
        );
        confirmedAppointments.push(appointment);
      }
      return { confirmedWaitlist, confirmedAppointments };
    } catch (error) {
      return rejectWithValue({ confirmedWaitlist, confirmedAppointments });
    }
  }
);

const initialState: PocState = {
  loading: false,
  updating: false,
  error: null,
  errorMessage: null,
  booking: false,
  bookingLoading: false,
  confirmed: false,
  pocInfo: {
    gap: 0,
    numOfAppointments: 0,
    appointments: [],
    periodStart: "",
    periodEnd: "",
    currentWeek: 0,
    totalWeeks: 0
  },
  providerInfo: [],
  preferredSupervisingProvider: null,
  preferredNonSupervisingProvider: null,
  complete: false,
  bookingState: {
    currentAppointment: 1,
    appointmentBooked: [],
    waitlist: []
  }
};

const bookPOCSlice = createSlice({
  name: "BookPOC",
  initialState,
  reducers: {
    setBooking: (state, action) => {
      state.booking = action.payload;
    },
    setBookingState: (state, action) => {
      state.bookingState = action.payload;
    },
    setComplete: (state, action) => {
      state.complete = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPeriod.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchPeriod.fulfilled, (state, action) => {
      state.loading = false;
      state.pocInfo = action.payload.pocInfo;
      state.providerInfo = action.payload.providerInfo;
      state.preferredSupervisingProvider =
        action.payload.preferredSupervisingProvider;
      state.preferredNonSupervisingProvider =
        action.payload.preferredNonSupervisingProvider;
      state.booking = action.payload.pocInfo.appointments.some(
        (appointment) => appointment.status === "unscheduled"
      );
      state.bookingState.currentAppointment = 1;
      state.bookingState.appointmentBooked = [];
      state.bookingState.waitlist = [];
      state.confirmed = false;
    });
    builder.addCase(fetchPeriod.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.error || null;
      state.errorMessage = action.payload?.errorMessage || null;
      if (
        action.payload?.error === "Your plan of care has been fully scheduled."
      ) {
        state.complete = true;
      }
    });
    builder.addCase(updatePeriod.pending, (state) => {
      state.updating = true;
    });
    builder.addCase(updatePeriod.fulfilled, (state) => {
      state.updating = false;
      state.booking = false;
    });
    builder.addCase(updatePeriod.rejected, (state, action) => {
      state.updating = false;
      state.error = action.payload?.error || null;
      state.errorMessage = action.payload?.errorMessage || null;
    });
    builder.addCase(bookAppointments.pending, (state) => {
      state.bookingLoading = true;
    });
    builder.addCase(bookAppointments.fulfilled, (state, action) => {
      const confirmedWaitlist = action.payload.confirmedWaitlist;
      let updatedWaitlist = state.bookingState.waitlist.map((waitlist) => {
        if (
          confirmedWaitlist.find(
            (confirmed) =>
              confirmed.appointmentNumber === waitlist.appointmentNumber
          )
        ) {
          return { ...waitlist, confirmed: true };
        }
        return waitlist;
      });
      state.bookingState.waitlist = updatedWaitlist;
      const confirmedAppointments = action.payload.confirmedAppointments;
      let updatedAppointments = state.bookingState.appointmentBooked.map(
        (appointment) => {
          if (
            confirmedAppointments.find(
              (confirmed) =>
                confirmed.appointmentNumber === appointment.appointmentNumber
            )
          ) {
            return { ...appointment, confirmed: true };
          }
          return appointment;
        }
      );
      state.bookingState.appointmentBooked = updatedAppointments;
      state.bookingLoading = false;
      state.confirmed = true;
    });
    builder.addCase(bookAppointments.rejected, (state, action) => {
      state.bookingLoading = false;
      const confirmedWaitlist = action.payload?.confirmedWaitlist || [];
      let updatedWaitlist = state.bookingState.waitlist.map((waitlist) => {
        if (
          confirmedWaitlist.find(
            (confirmed) =>
              confirmed.appointmentNumber === waitlist.appointmentNumber
          )
        ) {
          return { ...waitlist, confirmed: true };
        }
        return { ...waitlist, error: true };
      });
      state.bookingState.waitlist = updatedWaitlist;
      const confirmedAppointments = action.payload?.confirmedAppointments || [];
      let updatedAppointments = state.bookingState.appointmentBooked.map(
        (appointment) => {
          if (
            confirmedAppointments.find(
              (confirmed) =>
                confirmed.appointmentNumber === appointment.appointmentNumber
            )
          ) {
            return { ...appointment, confirmed: true };
          }
          return { ...appointment, error: true };
        }
      );
      state.bookingState.appointmentBooked = updatedAppointments;
    });
  }
});

export const { setBookingState, setBooking, setComplete } =
  bookPOCSlice.actions;
export default bookPOCSlice.reducer;
